import type { Role } from '@hemmafixa/shared';
import {
  BadgeCheck,
  Briefcase,
  Building2,
  CreditCard,
  ClipboardCheck,
  Wallet,
  Flag,
  HandCoins,
  LayoutDashboard,
  MessageSquare,
  ScrollText,
  Search,
  Settings,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { Messages } from '@/i18n/sv';

export type Area = Role;
export const AREAS: Area[] = ['client', 'provider', 'admin'];

/** Counts shown as badges in the menu */
export type NavBadge = 'jobsInReview' | 'providersPending' | 'unreadMessages' | 'openReports';

export interface NavEntry {
  label: keyof Messages['nav'];
  path: string;
  icon: LucideIcon;
  badge?: NavBadge;
  /** Starts a new group in the menu, below a divider */
  group?: boolean;
}

// One menu per area. Paths are absolute; the first entry is the area's home. Only screens on the real API are listed;
// the demo system screen comes back with its task.
export const navigation: Record<Area, NavEntry[]> = {
  client: [
    { label: 'overview', path: '/client', icon: LayoutDashboard },
    { label: 'myJobs', path: '/client/jobs', icon: Briefcase },
    { label: 'messages', path: '/client/messages', icon: MessageSquare, badge: 'unreadMessages' },
    { label: 'settings', path: '/client/settings', icon: Settings, group: true },
  ],
  provider: [
    { label: 'overview', path: '/provider', icon: LayoutDashboard },
    { label: 'findJobs', path: '/provider/jobs', icon: Search },
    { label: 'myProposals', path: '/provider/proposals', icon: HandCoins },
    { label: 'messages', path: '/provider/messages', icon: MessageSquare, badge: 'unreadMessages' },
    { label: 'companyProfile', path: '/provider/onboarding', icon: Building2, group: true },
    { label: 'subscription', path: '/provider/billing', icon: CreditCard },
    { label: 'settings', path: '/provider/settings', icon: Settings },
  ],
  admin: [
    { label: 'overview', path: '/admin', icon: LayoutDashboard },
    { label: 'review', path: '/admin/review', icon: ClipboardCheck, badge: 'jobsInReview' },
    { label: 'providers', path: '/admin/providers', icon: BadgeCheck, badge: 'providersPending' },
    { label: 'users', path: '/admin/users', icon: Users },
    { label: 'reports', path: '/admin/reports', icon: Flag, badge: 'openReports' },
    { label: 'billing', path: '/admin/billing', icon: Wallet },
    { label: 'auditLog', path: '/admin/audit', icon: ScrollText, group: true },
  ],
};

/** The menu entry for a path: the longest matching path, so /client/jobs/j1 highlights My jobs. */
export function activeEntry(area: Area, pathname: string) {
  return navigation[area]
    .filter((entry) => pathname === entry.path || pathname.startsWith(`${entry.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0];
}

/** Where the top bar search sends each area */
export const searchPath: Partial<Record<Area, string>> = {
  client: '/client/jobs',
  provider: '/provider/jobs',
  admin: '/admin/review',
};
